<template>
  <!-- 遮罩层 -->
  <view class="sku-mask" v-if="visible" @click="handleClose">
    <!-- 弹窗主体 (阻止冒泡) -->
    <view class="sku-content" @click.stop>
      <!-- 关闭按钮 -->
      <view class="close-btn" @click="handleClose">×</view>

      <!-- 1. 商品头部信息 -->
      <view class="header-section">
        <img
          class="product-img"
          :src="comapi.getImageUrl(product.cover_image)"
          mode="aspectFill"
        />
        <view class="product-info">
          <text class="title">{{ product.name || "商品名称" }}</text>
          <view class="price-box">
            <text class="symbol">¥</text>
            <text class="price">{{
              selectedSku?.shop_purchase_price / 100 || "0.00"
            }}</text>
            <text class="unit">/{{ selectedSku?.unit || "件" }}</text>
          </view>
        </view>
      </view>

      <!-- 2. 规格选择 -->
      <view class="spec-section">
        <text class="label">规格</text>
        <view class="spec-list">
          <view
            v-for="sku in product.skus"
            :key="sku.uuid"
            class="spec-item"
            :class="{ active: currentSpec === sku.uuid }"
            @click="selectSpec(sku)"
          >
            {{ sku.spec_name }}
          </view>
        </view>
      </view>

      <!-- 3. 数量选择 -->
      <view class="quantity-section">
        <text class="label">数量</text>
        <view class="stepper">
          <view
            class="btn minus"
            :class="{ disabled: quantity <= 1 }"
            @click="changeQuantity(-1)"
            >−</view
          >
          <view class="input">{{ quantity }}</view>
          <view
            class="btn plus"
            :class="{
              disabled:
                quantity >=
                product?.skus?.find((v) => v.uuid === currentSpec)
                  ?.salable_stock,
            }"
            @click="changeQuantity(1)"
            >+</view
          >
        </view>
      </view>

      <!-- 4. 底部按钮组 -->
      <view class="footer-actions">
        <button class="scoreaction-btn buy-btn" @click="handleSubmit">
          加入购物车
        </button>
      </view>
    </view>
  </view>
</template>

<script setup>
import { ref, watch } from "vue";
import comapi from "@/utils/api/com";
import { showFailToast } from "vant";

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  productInfo: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["close", "selected", "confirm"]);

// 响应式状态
const visible = ref(props.show);
const product = ref({});
const currentSpec = ref(null);
const quantity = ref(1);
const selectedSku = ref(null);

// 监听弹窗显示状态
watch(
  () => props.show,
  (val) => {
    visible.ref = val; // 兼容处理，实际应直接用 props.show 或本地 visible
    visible.value = val;
    if (val) {
      quantity.value = 1;
      product.value = props.productInfo || {};

      const skus = product.value.skus;
      if (skus && skus.length > 0) {
        currentSpec.value = skus[0].uuid;
        selectedSku.value = skus[0];
      } else {
        currentSpec.value = null;
        selectedSku.value = null;
      }
    }
  },
);

const handleClose = () => {
  emit("close");
};

const selectSpec = (sku) => {
  const skuId = sku.uuid;
  currentSpec.value = skuId;
  selectedSku.value = sku;
  emit("selected", skuId);
};

const changeQuantity = (step) => {
  const newVal = quantity.value + step;
  if (newVal < 1) return;
  quantity.value = newVal;
};

const handleSubmit = () => {
  if (!selectedSku.value) return;

  const skuId = selectedSku.value.uuid;
  let cartList = JSON.parse(localStorage.getItem("cart_data_list") || "[]");
  let index = cartList.findIndex((item) => item.uuid === skuId);

  if (index !== -1) {
    cartList[index].num += quantity.value;
    cartList[index].shop_purchase_price =
      selectedSku.value.shop_purchase_price || "0.00";
  } else {
    cartList.push({
      uuid: skuId,
      product_uuid: product.value.uuid,
      product_name: product.value.name,
      spec_name: selectedSku.value.spec_name,
      unit: selectedSku.value.unit,
      shop_purchase_price: selectedSku.value.shop_purchase_price || "0.00",
      num: quantity.value,
      cover_image: product.value.cover_image,
    });
  }

  localStorage.setItem("cart_data_list", JSON.stringify(cartList));
  emit("confirm");
  handleClose();
};
</script>

<style lang="less" scoped>
/* 375px 设计稿换算: 原rpx / 2 = px */

.sku-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 999;
  display: flex;
  align-items: flex-end;
}

.sku-content {
  width: 100%;
  min-height: 40vh;
  background-color: #fff;
  border-radius: 12px 12px 0 0;
  padding: 20px 15px 30px;
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-direction: column;

  .close-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    font-size: 25px;
    color: #999;
    padding: 5px;
    line-height: 1;
    z-index: 10;
  }

  .header-section {
    display: flex;
    margin-bottom: 30px;

    .product-img {
      width: 105px;
      height: 105px;
      border-radius: 8px;
      background-color: #fff;
      margin-right: 10px;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
    }

    .product-info {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      padding-top: 5px;

      .title {
        font-size: 16px;
        font-weight: bold;
        color: #333;
        margin-bottom: 10px;
      }

      .price-box {
        color: #9f3411;
        font-weight: bold;
        display: flex;
        align-items: baseline;

        .symbol {
          font-size: 22px;
          margin-right: 2px;
        }

        .price {
          font-size: 22px;
        }

        .unit {
          font-size: 13px;
          color: #999;
          font-weight: normal;
          margin-left: 3px;
        }
      }
    }
  }

  .spec-section {
    margin-bottom: 25px;

    .label {
      font-size: 15px;
      font-weight: bold;
      color: #333;
      margin-bottom: 12px;
      display: block;
    }

    .spec-list {
      display: flex;
      flex-wrap: wrap;

      .spec-item {
        padding: 6px 20px;
        background-color: #f6f7fb;
        border: 1px solid transparent;
        border-radius: 2px;
        font-size: 14px;
        color: #333;
        margin-right: 12px;
        margin-bottom: 10px;
        transition: all 0.2s;

        &.active {
          background-color: #fff;
          border-color: #dcbb89;
          color: #dcbb89;
        }
      }
    }
  }

  .quantity-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: auto;

    .label {
      font-size: 15px;
      font-weight: bold;
      color: #333;
    }

    .stepper {
      display: flex;
      align-items: center;

      .btn {
        width: 30px;
        height: 30px;
        background-color: #ffffff;
        border: 1px solid #e0e0e0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        color: #666;
        border-radius: 4px;

        &.minus {
          border-radius: 4px 0 0 4px;
          border-right: none;
        }

        &.plus {
          border-radius: 0 4px 4px 0;
          border-left: none;
        }

        &.disabled {
          color: #ccc;
          background-color: #f8f8f8;
        }
      }

      .input {
        width: 40px;
        height: 30px;
        background-color: #ffffff;
        border-top: 1px solid #e0e0e0;
        border-bottom: 1px solid #e0e0e0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        color: #333;
      }
    }
  }

  .footer-actions {
    margin-top: 40px;
    display: flex;
    justify-content: space-between;
    padding-bottom: env(safe-area-inset-bottom);

    .scoreaction-btn {
      width: 100%;
      height: 46px;
      border-radius: 60px;
      font-size: 15px;
      font-weight: bold;
      text-align: center;
      margin: 0;
      background-color: #dcbb89;
      color: #ffffff;
      border: none;

      &::after {
        border: none;
      }
    }
  }
}
</style>
